<template>
  <div class="js-code">
    <div class="js-gutter" ref="gutter" aria-hidden="true">
      <span v-for="n in lineCount" :key="n">{{ n }}</span>
    </div>
    <div class="js-pane">
      <pre class="js-highlight" ref="highlight" aria-hidden="true" v-html="highlighted"></pre>
      <textarea
        ref="input"
        class="js-input"
        :value="modelValue"
        spellcheck="false"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        wrap="off"
        :aria-label="ariaLabel"
        @input="onInput"
        @keydown="onKey"
        @scroll="onScroll"
      ></textarea>
    </div>
  </div>
</template>

<script>

import { highlightJavaScript } from '../lib/js-highlight.js'

export default {
  name: 'JsCodeEditor',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: 'JavaScript'
    }
  },
  emits: ['update:modelValue', 'save'],
  computed: {
    lineCount () {

      return String(this.modelValue || '').split('\n').length
    },
    highlighted () {

      const html = highlightJavaScript(this.modelValue || '')

      return html || ' '
    }
  },
  methods: {
    focus () {

      if (this.$refs.input) this.$refs.input.focus()
    },
    onInput (event) {

      this.$emit('update:modelValue', event.target.value)
    },
    onScroll (event) {

      const top = event.target.scrollTop
      const left = event.target.scrollLeft

      if (this.$refs.highlight) {

        this.$refs.highlight.scrollTop = top
        this.$refs.highlight.scrollLeft = left
      }

      if (this.$refs.gutter) this.$refs.gutter.scrollTop = top
    },
    onKey (event) {

      if ((event.metaKey || event.ctrlKey) && event.key === 's') {

        event.preventDefault()
        this.$emit('save')

        return
      }

      if (event.key === 'Tab') {

        event.preventDefault()
        this.insertTab(event)

        return
      }

      if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey && !event.altKey) {

        event.preventDefault()
        this.insertNewline(event)
      }
    },
    replace (next, cursor) {

      this.$emit('update:modelValue', next)
      this.$nextTick(() => {

        const el = this.$refs.input

        if (!el) return

        if (el.value !== next) el.value = next

        el.selectionStart = cursor
        el.selectionEnd = cursor
      })
    },
    insertTab (event) {

      const el = event.target
      const value = this.modelValue || ''
      const start = el.selectionStart
      const end = el.selectionEnd

      if (event.shiftKey) {

        const lineStart = value.lastIndexOf('\n', start - 1) + 1
        const indent = value.slice(lineStart, lineStart + 2) === '  ' ? 2 : 0

        if (!indent) return

        const next = value.slice(0, lineStart) + value.slice(lineStart + indent)

        this.replace(next, Math.max(lineStart, start - indent))

        return
      }

      const next = value.slice(0, start) + '  ' + value.slice(end)

      this.replace(next, start + 2)
    },
    insertNewline (event) {

      const el = event.target
      const value = this.modelValue || ''
      const start = el.selectionStart
      const lineStart = value.lastIndexOf('\n', start - 1) + 1
      const indent = (value.slice(lineStart, start).match(/^[ ]*/) || [''])[0]
      const insert = `\n${indent}`
      const next = value.slice(0, start) + insert + value.slice(el.selectionEnd)

      this.replace(next, start + insert.length)
    }
  }
}

</script>

<style scoped>

.js-code {
  display: flex;
  min-height: 0;
  height: 100%;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  background: #10100e;
  overflow: hidden;
}

.js-gutter {
  flex: 0 0 2.75rem;
  overflow: hidden;
  padding: 0.7rem 0.45rem 0.7rem 0.2rem;
  background: #161614;
  border-right: 1px solid #2a2a27;
  color: #5c5c5a;
  text-align: right;
  font: 13px/1.5 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  user-select: none;
}

.js-gutter span {
  display: block;
}

.js-pane {
  position: relative;
  flex: 1;
  min-width: 0;
}

.js-highlight,
.js-input {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0.7rem 0.8rem;
  border: 0;
  font: 13px/1.5 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  tab-size: 2;
  white-space: pre;
  word-wrap: normal;
  overflow: auto;
}

.js-highlight {
  color: #d4d4d2;
  pointer-events: none;
  overflow: hidden;
}

.js-input {
  color: transparent;
  caret-color: #f3f3f1;
  background: transparent;
  resize: none;
  outline: none;
}

.js-input::selection {
  background: rgba(154, 209, 43, 0.28);
}

</style>
